import { getAnalytics, logEvent as firebaseLogEvent, setAnalyticsCollectionEnabled, type Analytics } from 'firebase/analytics';
import { app } from './firebase';

let analyticsInstance: Analytics | null = null;

// Ensure gtag function is available (initialized in index.html)
declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

function gtag(...args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/**
 * Update Google Consent Mode v2 signals.
 * Called when user accepts or rejects the privacy policy.
 */
export function updateConsentMode(granted: boolean): void {
  const status = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    'analytics_storage': status,
    'ad_storage': status,
    'ad_user_data': status,
    'ad_personalization': status,
  });
}

/**
 * Initialize Google Analytics (via Firebase).
 * Should only be called after the user has accepted the privacy policy.
 */
export function initializeAnalytics(): Analytics | null {
  if (analyticsInstance) return analyticsInstance;

  try {
    // Grant consent signals before initializing
    updateConsentMode(true);

    analyticsInstance = getAnalytics(app);
    setAnalyticsCollectionEnabled(analyticsInstance, true);
    return analyticsInstance;
  } catch (err) {
    console.warn('Firebase Analytics initialization failed:', err);
    return null;
  }
}

/**
 * Disable analytics collection.
 * Called when user has not consented or revokes consent.
 */
export function disableAnalytics(): void {
  // Revoke consent signals
  updateConsentMode(false);

  if (analyticsInstance) {
    setAnalyticsCollectionEnabled(analyticsInstance, false);
    analyticsInstance = null;
  }
}

/**
 * Log a custom event to Google Analytics.
 * No-ops if analytics is not initialized.
 */
export function logEvent(eventName: string, params?: Record<string, unknown>): void {
  if (!analyticsInstance) return;
  firebaseLogEvent(analyticsInstance, eventName, params);
}

/**
 * Get the current analytics instance (or null if not initialized).
 */
export function getAnalyticsInstance(): Analytics | null {
  return analyticsInstance;
}
